<template>
  <div class="content">
    <h1 class="has-text-centered">Netzwerkmanagement mithilfe von DHCP und SNMP</h1>
        <div class="box">
      <p>Eine kompakte Zusammenfassung über das Thema Netzwerkmanagement mithilfe von DHCP (<strong>D</strong>ynamic <strong>H</strong>ost <strong>C</strong>onfiguration <strong>P</strong>rotocol) und SNMP (<strong>S</strong>imple <strong>N</strong>etwork <strong>M</strong>anagement <strong>P</strong>rotocol).</p>
      <FAQsTableOfContents :faqs="faqs" />
    </div>

    <div class="box">
      <FAQs :faqs="faqs" :h_level="2" />
    </div>

    <FeedbackBox />
  </div>
</template>

<script>
import FeedbackBox from '@/components/FeedbackBox.vue'
import FAQs from '@/components/FAQs.vue'
import FAQsTableOfContents from '@/components/FAQsTableOfContents.vue'

export default {
  name: 'theory-network-management',
  components: {
    FeedbackBox,
    FAQs,
    FAQsTableOfContents
  },
  metaInfo: {
    title: ' Netzwerkmanagement mithilfe von DHCP und SNMP',
    meta: [
      {
        name: 'description',
        content: 'Eine kompakte Zusammenfassung über das Thema Netzwerkmanagement mithilfe von DHCP und SNMP.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'network-engineering' })
  },
  data () {
    return {
      faqs: [
        {
          question: 'Was ist Netzwerkmanagement?',
          answer:
            '<p>Verwaltung, Betriebstechnik und Überwachung (OAM = <strong>O</strong>peration, <strong>A</strong>dministration and <strong>M</strong>aintenance) von IT-Netzwerken und Telekommunikationsnetzen.</p>'
        },
        {
          question: 'Was ist das FCAPS-Modell?',
          answer:
            '<p>Das FCAPS-Modell von ISO beschreibt die funktionalen Aufgaben von Netzwerkmanagement.</p>' +
            '<ul>' +
            '  <li>' +
            '    <strong>F</strong>ault Management (Fehlermanagement): Erkennen, Protokollieren, Melden und Beheben von auftretenden Fehlerzuständen' +
            '  </li>' +
            '  <li>' +
            '    <strong>C</strong>onfiguration Management (Konfigurationsmanagement): Erfassung aller Komponenten (Configuration Items)  die überwacht werden müssen' +
            '  </li>' +
            '  <li>' +
            '    <strong>A</strong>ccounting Management (Abrechnungsmanagement): Erfassen der Benutzung des Netzes, so dass Rechnungen gestellt werden können' +
            '  </li>' +
            '  <li>' +
            '    <strong>P</strong>erformance Management (Leistungsmanagement): Verkehrswerte/Leistungsdaten sammeln und Statistiken führen, Grenzwerte festlegen' +
            '  </li>' +
            '  <li>' +
            '    <strong>S</strong>ecurity Management (Sicherheitsmanagement): Authentifizierung von Benutzern, Autorisierung von Zugriff und Nutzung' +
            '  </li>' +
            '</ul>'
        },
        {
          question: 'Was ist DHCP?',
          answer:
            '<ul>' +
            '  <li>DHCP = <strong>D</strong>ynamic <strong>H</strong>ost <strong>C</strong>onfiguration <strong>P</strong>rotocol</li>' +
            '  <li>Befindet sich auf dem Application Layer des OSI-Schichtenmodells</li>' +
            '  <li>Protokoll zur Verwaltung IP-Adressen in einem TCP/IP-Netzwerk</li>' +
            '  <li>Durch DHCP kann sich jeder Teilnehmer im Netzwerk selbst konfigurieren</li>' +
            '</ul>' +
            '<h3>Funktionsweise von DHCP</h3>' +
            '<div class="has-text-centered">' +
            '  <img src="/img/theory/OSI-Modell.png">' +
            '</div>' +
            '<ol>' +
            '  <li>Ein Client kommuniziert mit einem Server</li>' +
            '  <li>Der Client bittet Server um eine IP-Konfiguration</li>' +
            '  <li>Die übernommene Konfiguration ist eine bestimmte Zeit lang gültig (Lease-Time)</li>' +
            '  <li>Nach der Hälfte der Lease-Time muss eine neue Konfiguration angefordert werden (normalerweise schickt der DHCP-Server wieder dieselbe Konfiguration)</li>' +
            '</ol>'
        },
        {
          question: 'Welche DHCP-Nachrichten gibt es?',
          answer:
            '<ul>' +
            '  <li>' +
            '    <strong>DHCP-DISCOVER</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Ein Client ohne IP-Adresse sendet eine Broadcast-Anfrage nach Adress-Angeboten an alle DHCP-Server im lokalen Netz</li>' +
            '    <li>Dabei handelt es sich um ein UDP-Paket mit der Ziel-Adresse 255.255.255.255 und der Quell-Adresse 0.0.0.0</li>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-OFFER</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Die Antwort eines Servers auf eine DHCPDISCOVER-Anfrage</li>' +
            '    <li>Ein UDP-Paket mit folgenden Informationen:</li>' +
            '    <ul>' +
            '      <li>MAC-Adresse des Clients</li>' +
            '      <li>mögliche IP-Adresse</li>' +
            '      <li>Laufzeit der IP-Adresse/dieser Konfiguration (Lease-Time)</li>' +
            '      <li>Subnetzmaske</li>' +
            '      <li>IP-Adresse des DHCP-Servers</li>' +
            '    </ul>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-REQUEST</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Der Client fordert eine der angebotenen IP-Adressen an</li>' +
            '    <li>Nachricht geht an alle Server damit alle über die Annahme der freien IP-Adresse in Kenntnis gesetzt sind</li>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-ACK</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Server bestätigt den DHCPREQUEST des Clients oder die Übermittlung von Konfigurationsparametern mittels DHCPINFORM</li>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-NAK</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Server lehnt den DHCPREQUEST des Clients ab</li>' +
            '    <li>Mögliche Gründe</li>' +
            '    <ul>' +
            '      <li>Keine freie IP-Adresse</li>' +
            '      <li>Während der Kommunikation wurde die angeforderte Konfiguration bereits an einen anderen Client vergeben</li>' +
            '    </ul>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-DECLINE</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Ablehnung durch den Client, da die IP-Adresse schon verwendet wird</li>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-DECLINE</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Ablehnung durch den Client, da die IP-Adresse schon verwendet wird</li>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-RELEASE</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Der Client gibt die eigene Konfiguration wieder für andere Clients frei</li>' +
            '  </ul>' +
            '  <li>' +
            '    <strong>DHCP-INFORM</strong>' +
            '  </li>' +
            '  <ul>' +
            '    <li>Anfrage des Clients nach weiteren Konfigurationsparametern (z.B. weil der Client eine statische IP-Adresse besitzt)</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Was ist SNMP?',
          answer:
            '<ul>' +
            '  <li>SNPM = <strong>S</strong>imple <strong>N</strong>etwork <strong>M</strong>anagement <strong>P</strong>rotocol</li>' +
            '  <li>Befindet sich auf dem Application Layer des OSI-Schichtenmodells</li>' +
            '  <li>Protokoll zur Steuerung und Verwaltung von Netzwerken</li>' +
            '</ul>' +
            '<h3>Einsatzgebiet</h3>' +
            '<ul>' +
            '  <li>Überwachung von Netzwerkkomponenten</li>' +
            '  <li>Fernsteuerung und Fernkonfiguration von Netzwerkkomponenten</li>' +
            '  <li>Fehlererkennung und Fehlerbenachrichtigung</li>' +
            '</ul>' +
            '<h3>Die Funktionsweise von SNMP</h3>' +
            '<div class="has-text-centered">' +
            '  <img src="/img/theory/SNMP-Funktionsweise.png">' +
            '</div>' +
            '<ul>' +
            '  <li>Agenten können Software- oder Hardware-basiert sein - ihre Aufgabe besteht in der Überwachung des Netzwerkgerätes (können auch Einstellungen vornehmen oder Aktionen auslösen)</li>' +
            '  <li>Mithilfe von SNMP kommuniziert die Management Station mit den Agenten über das Netzwerk</li>' +
            '  <li>Das verbindungslose UDP (<strong>U</strong>ser <strong>D</strong>atagram <strong>P</strong>rotocol) wird verwendet um die Netzwerkbelastung gering zu halten</li>' +
            '  <li>In der MIB (<strong>M</strong>anagement <strong>I</strong>nformation <strong>B</strong>ase) werden die OIDs (<strong>O</strong>bject <strong>Id</strong>entifier) dokumentiert</li>' +
            '</ul>'
        },
        {
          question: 'Welche SNMP-Nachrichten gibt es?',
          answer:
            '<ul>' +
            '  <li><strong>GET-REQUEST</strong></li>' +
            '  <ul>' +
            '    <li>Wird von Management Station verschickt</li>' +
            '    <li>Anfordern eines spezifischen Datensatzes</li>' +
            '  </ul>' +
            '  <li><strong>GETNEXT-REQUEST</strong></li>' +
            '  <ul>' +
            '    <li>Wird von Management Station verschickt</li>' +
            '    <li>Ruft einen nachfolgenden Datensatz ab (um Tabellen zu durchlaufen)</li>' +
            '  </ul>' +
            '  <li><strong>GETBULK</strong></li>' +
            '  <ul>' +
            '    <li>Ab SNMPv2</li>' +
            '    <li>Wird von Management Station verschickt</li>' +
            '    <li>Ruft mehrere Datensätze auf einmal ab</li>' +
            '    <li>Ähnelt mehreren GETNEXT-REQUESTs</li>' +
            '  </ul>' +
            '  <li><strong>SET-REQUEST</strong></li>' +
            '  <ul>' +
            '    <li>Wird von Management Station verschickt</li>' +
            '    <li>Verändern von einem oder mehreren Datensätzen</li>' +
            '    <li>Damit kann die Management Station Werte bei einem Agenten anpassen</li>' +
            '  </ul>' +
            '  <li><strong>GET-RESPONSE</strong></li>' +
            '  <ul>' +
            '    <li>Wird von einem Agenten verschickt</li>' +
            '    <li>Antwort auf eine GET/SET-Nachricht</li>' +
            '  </ul>' +
            '  <li><strong>TRAP</strong></li>' +
            '  <ul>' +
            '    <li>Wird von einem Agenten verschickt</li>' +
            '    <li>Unaufgeforderte Nachricht um bei Auftritt eines Ereignisses die Management Station zu informieren</li>' +
            '    <li>Wird nicht von Management Station bestätigt</li>' +
            '  </ul>' +
            '</ul>'
        }
      ]
    }
  }
}
</script>
